import React, { useEffect, useState, useRef, useCallback } from "react";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import { useNavigate } from "react-router-dom";

// Neo4j-Style Graph Component using vis-network
const VisNetworkGraphComponent = ({ graphData, selectedCommand, onNodeClick, userPath = [] }) => {
  const networkRef = useRef(null);
  const networkInstanceRef = useRef(null);
  const nodeDataSetRef = useRef(null);
  const edgeDataSetRef = useRef(null);
  const [networkStatus, setNetworkStatus] = useState('Initializing...');

  // Only create the network when graphData changes
  useEffect(() => {
    if (!networkRef.current || !graphData || graphData.length === 0) {
      setNetworkStatus('Waiting for data...');
      return;
    }
    setNetworkStatus('Creating graph...');
    try {
      const nodes = [];
      const edges = [];
      graphData.forEach((item, index) => {
        if (item.type === 'node') {
          const nodeData = item.data;
          const props = nodeData.properties || {};
          const nodeLabels = nodeData.labels || [];
          // ... (same color/size logic as before, using userPath for initial highlight)
          let color = '#FFFFFF';
          let size = 25;
          let borderColor = '#EF4444';
          const nodeIndex = userPath.indexOf(nodeData.id);
          const isInPath = nodeIndex !== -1;
          const isRootNode = nodeIndex === 0;
          const nodeName = (props.name || '').toLowerCase();
          const nodeCategory = (props.category || '').toLowerCase();
          const hasRecommendationTag = nodeLabels.some(label => 
            label === 'RecommendationStep' || 
            label.toLowerCase().includes('recommendationstep') || 
            label.toLowerCase().includes('recommendation')
          ) || (props.type && (props.type === 'RecommendationStep' || props.type.toLowerCase().includes('recommendation')));
          const hasTroubleshootingTag = nodeLabels.some(label => 
            label === 'TroubleshootingStep' ||
            label.toLowerCase().includes('troubleshootingstep') || 
            label.toLowerCase().includes('troubleshooting') ||
            label === 'Diagnosis' ||
            label.toLowerCase().includes('diagnosis') ||
            (label === 'Step' && nodeLabels.some(l => l.toLowerCase().includes('diagnosis')))
          ) || (props.type && (props.type === 'TroubleshootingStep' || props.type.toLowerCase().includes('troubleshooting')));
          const isTroubleshooting = hasTroubleshootingTag || nodeName.includes('troubleshoot') || nodeName.includes('Diagnosis') || nodeCategory.includes('troubleshoot') || nodeCategory.includes('diagnosis');
          const isRecommendation = hasRecommendationTag || nodeName.includes('recommend') || nodeCategory.includes('recommend');
          if (isRootNode) {
            color = '#8B5CF6';
            borderColor = '#7C3AED';
            size = 35;
          } else {
            if (isTroubleshooting) {
              color = '#90d5ff';
              size = 28;
            } else if (isRecommendation) {
              color = '#F59E0B';
              size = 28;
            } else {
              color = '#E5E7EB';
              size = 25;
            }
            if (isInPath) {
              borderColor = '#10B981';
              size += 4;
            } else {
              borderColor = '#EF4444';
            }
            if (nodeData.nodeType === 'terminal') {
              color = '#FF6B6B';
              borderColor = isInPath ? '#10B981' : '#EF4444';
              size = 32;
            }
          }
          nodes.push({
            id: nodeData.id,
            label: props.step_title || props.name || props.command || 'Node',
            title: `<div style="padding: 8px; font-family: 'Segoe UI', sans-serif;"><strong style="color: #333; font-size: 14px;">${props.name || 'Node'}</strong>${props.command ? `<br/><span style="color: #666; font-size: 12px;">Command: <code>${props.command}</code></span>` : ''}${props.condition ? `<br/><span style="color: #666; font-size: 12px;">Condition: ${props.condition}</span>` : ''}${props.category ? `<br/><span style="color: #888; font-size: 11px;">Type: ${props.category}</span>` : ''}</div>`,
            color: {
              background: color,
              border: borderColor,
              highlight: { background: color, border: '#FFD23F' },
              hover: { background: color, border: '#FFD23F' }
            },
            size,
            font: {
              color: '#000000',
              size: 12,
              face: 'Segoe UI, Arial, sans-serif',
              strokeWidth: 1,
              strokeColor: '#000000'
            },
            shape: 'dot',
            borderWidth: 2,
            shadow: {
              enabled: true,
              color: 'rgba(0,0,0,0.2)',
              size: 8,
              x: 2,
              y: 2
            }
          });
        }
      });
      graphData.forEach(item => {
        if (item.type === 'relationship') {
          const relData = item.data;
          const props = relData.properties || {};
          const sourceInPath = userPath.includes(relData.source);
          const targetInPath = userPath.includes(relData.target);
          const isPathEdge = sourceInPath && targetInPath;
          let color = isPathEdge ? '#10B981' : '#A5ABB6';
          let width = isPathEdge ? 3 : 1.5;
          let dashes = false;
          if (!isPathEdge) {
            switch (relData.relationshipType) {
              case 'previous': color = '#4C8EDA'; width = 2; break;
              case 'next': color = '#57C7E3'; width = 2; break;
              case 'uses': color = '#F79767'; width = 1.5; dashes = [3, 3]; break;
            }
          }
          edges.push({
            id: relData.id,
            from: relData.source,
            to: relData.target,
            label: props.condition || '',
            color: { color, highlight: '#FFD23F', hover: '#FFD23F' },
            width,
            dashes,
            arrows: { to: { enabled: true, scaleFactor: 1.0, type: 'arrow' } },
            smooth: { enabled: true, type: 'curvedCCW', roundness: 0.05, forceDirection: 'none' },
            font: { color: '#2D3748', size: 13, face: 'Segoe UI, Arial, sans-serif', strokeWidth: 3, strokeColor: '#ffffff', align: 'middle', vadjust: 0 },
            length: 280
          });
        }
      });
      const nodeDataSet = new DataSet(nodes);
      const edgeDataSet = new DataSet(edges);
      nodeDataSetRef.current = nodeDataSet;
      edgeDataSetRef.current = edgeDataSet;
      const data = { nodes: nodeDataSet, edges: edgeDataSet };
      const options = {
        nodes: {
          shape: 'dot', size: 25, font: { size: 12, color: '#000000', face: 'Segoe UI, Arial, sans-serif' }, borderWidth: 2, shadow: { enabled: true, color: 'rgba(0,0,0,0.2)', size: 8, x: 2, y: 2 }
        },
        edges: {
          width: 2, color: { color: '#A5ABB6', highlight: '#FFD23F' }, arrows: { to: { enabled: true, scaleFactor: 1.0 } }, smooth: { enabled: true, type: 'continuous', roundness: 0.05, forceDirection: 'none' }, font: { color: '#2D3748', size: 13, face: 'Segoe UI, Arial, sans-serif', strokeWidth: 3, strokeColor: '#ffffff', align: 'middle', vadjust: 0 }, length: 100
        },
        physics: {
          enabled: true,
          stabilization: { enabled: true, iterations: 100, updateInterval: 25 },
          barnesHut: { 
            gravitationalConstant: -2000,
            centralGravity: 0.05,
            springLength: 200,
            springConstant: 0.03,
            damping: 0.3,  // Increased damping to reduce unwanted movement
            avoidOverlap: 0.3 
          }
        },
        interaction: { hover: true, tooltipDelay: 300, hideEdgesOnDrag: false, hideNodesOnDrag: false, zoomView: true, dragView: true },
        layout: { improvedLayout: true, clusterThreshold: 150, hierarchical: { enabled: false, levelSeparation: 300, nodeSpacing: 200, treeSpacing: 300, blockShifting: true, edgeMinimization: true, parentCentralization: true, direction: 'UD', sortMethod: 'directed' } }
      };
      if (networkInstanceRef.current) {
        networkInstanceRef.current.destroy();
        networkInstanceRef.current = null;
      }
      networkInstanceRef.current = new Network(networkRef.current, data, options);
      setNetworkStatus('Graph created successfully');
      networkInstanceRef.current.on('click', function(params) {
        if (params.nodes.length > 0) {
          const nodeId = params.nodes[0];
          const node = nodeDataSet.get(nodeId);
          const originalNodeData = graphData.find(item => item.type === 'node' && item.data.id === nodeId);
          if (originalNodeData && onNodeClick) {
            onNodeClick(originalNodeData.data);
          }
        }
      });
      // --- Unlock graph after stabilization ---
      networkInstanceRef.current.once('stabilizationIterationsDone', function() {
        networkInstanceRef.current.setOptions({ physics: false });
        networkInstanceRef.current.fit({ animation: { duration: 800, easingFunction: 'easeInOutQuad' } });
        networkInstanceRef.current.redraw();
        setNetworkStatus('Graph ready');
      });
      // --- Failsafe: forcibly disable physics and enable all interactions after 1s ---
      setTimeout(() => {
        if (networkInstanceRef.current) {
          networkInstanceRef.current.setOptions({
            physics: false,
            interaction: {
              hover: true,
              tooltipDelay: 300,
              hideEdgesOnDrag: false,
              hideNodesOnDrag: false,
              zoomView: true,
              dragView: true,
              dragNodes: true,
              selectable: true,
              selectConnectedEdges: true,
              multiselect: true,
              navigationButtons: true,
              keyboard: true
            }
          });
          setNetworkStatus('Graph ready');
        }
      }, 1000);
    } catch (error) {
      console.error('Error creating Neo4j-style graph:', error);
      setNetworkStatus('Error creating graph');
    }
  }, [graphData]);

  // Update highlights when userPath changes (but don't re-create the network)
  useEffect(() => {
    if (!networkInstanceRef.current || !nodeDataSetRef.current) return;
    // Update node border colors and sizes for traversed nodes
    const allNodes = nodeDataSetRef.current.get();
    allNodes.forEach(node => {
      const isInPath = userPath.indexOf(node.id) !== -1;
      const isRootNode = userPath[0] === node.id;
      let borderColor = isInPath ? '#10B981' : '#EF4444';
      let size = node.size;
      if (isRootNode) {
        borderColor = '#7C3AED';
        size = 35;
      } else if (isInPath) {
        size = node.size > 28 ? node.size : node.size + 4;
      } else {
        size = 25;
      }
      nodeDataSetRef.current.update({ id: node.id, color: { ...node.color, border: borderColor }, size });
    });
    // Update edge colors for path edges
    const allEdges = edgeDataSetRef.current.get();
    allEdges.forEach(edge => {
      const sourceInPath = userPath.includes(edge.from);
      const targetInPath = userPath.includes(edge.to);
      const isPathEdge = sourceInPath && targetInPath;
      const color = isPathEdge ? '#10B981' : '#A5ABB6';
      const width = isPathEdge ? 3 : 1.5;
      edgeDataSetRef.current.update({ id: edge.id, color: { ...edge.color, color }, width });
    });
  }, [userPath]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (networkInstanceRef.current) {
        networkInstanceRef.current.destroy();
        networkInstanceRef.current = null;
      }
    };
  }, []);

  // After the network is created and ready, fit the graph to the container
  useEffect(() => {
    if (networkStatus === 'Graph ready' && networkInstanceRef.current) {
      networkInstanceRef.current.fit({ animation: { duration: 500, easingFunction: 'easeInOutQuad' } });
    }
  }, [networkStatus]);

  // Debounced fit on window resize
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (networkInstanceRef.current) {
          networkInstanceRef.current.fit({ animation: { duration: 300, easingFunction: 'easeInOutQuad' } });
        }
      }, 300);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, []);

  // Remove the constrainToContainer function as it causes unwanted movement

  return (
    <div className="w-full">
      {/* Status indicator - only show if not ready */}
      {networkStatus !== 'Graph ready' && (
        <div className="text-xs text-gray-500 mb-2 flex items-center">
          {networkStatus === 'Creating graph...' && (
            <div className="animate-spin rounded-full h-3 w-3 border-b border-[#40B5AD] mr-2"></div>
          )}
          {networkStatus}
        </div>
      )}
      <div 
        ref={networkRef}
        className="w-full rounded-lg"
        style={{ 
          height: '700px',
          backgroundColor: '#F8F9FA',
          border: '1px solid #E1E5E9',
          minHeight: '650px'
        }}
      />
    </div>
  );
};

const AgentDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [escalatedSessions, setEscalatedSessions] = useState([]); // [{sessionId, userInfo}]
  const [activeSession, setActiveSession] = useState(null);
  const [activeSessionUserInfo, setActiveSessionUserInfo] = useState(null); // Store user info for active session
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [userCommands, setUserCommands] = useState([]); // New state for commands
  const [hasEscalationCommands, setHasEscalationCommands] = useState(false); // Flag to track escalation commands
  const hasEscalationCommandsRef = useRef(false); // Ref to track current value for event handlers
  const [selectedCommand, setSelectedCommand] = useState(null); // New state for selected command
  
  // Keep ref in sync with state
  useEffect(() => {
    hasEscalationCommandsRef.current = hasEscalationCommands;
  }, [hasEscalationCommands]);
  const [selectedNode, setSelectedNode] = useState(null); // New state for selected node
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [showCommandOutput, setShowCommandOutput] = useState(false);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'indented'
  const [showSummary, setShowSummary] = useState(false);
  const [conversationSummary, setConversationSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showGraphSummary, setShowGraphSummary] = useState(false);
  const [graphSummaryLoading, setGraphSummaryLoading] = useState(false);
  const [aiGraphAnalysis, setAiGraphAnalysis] = useState('');
  const chatBodyRef = useRef(null);
  
  // Theme state management
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  // Resizable panels state
  const [graphChatSplit, setGraphChatSplit] = useState(60); // Graph gets 60%, Chat gets 40%
  const [isResizingGraphChat, setIsResizingGraphChat] = useState(false);
  const isResizingRef = useRef(false);
  
  // Theme configuration
  const theme = {
    dark: {
      bg: 'bg-black',
      sidebarBg: 'bg-black',
      cardBg: 'bg-black',
      headerBg: 'bg-black',
      contentBg: 'bg-black',
      inputBg: 'bg-gray-900',
      text: 'text-white',
      textSecondary: 'text-gray-300',
      textMuted: 'text-gray-400',
      timestampColor: 'text-white',
      border: 'border-gray-800',
      borderLight: 'border-gray-700',
      hover: 'hover:bg-gray-900',
      buttonBg: 'bg-gray-900',
      buttonHover: 'hover:bg-gray-800',
      accent: 'bg-[#40B5AD]',
      accentHover: 'hover:bg-[#359691]',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    },
    light: {
      bg: 'bg-[#fafaf5]',
      sidebarBg: 'bg-[#fafaf5]',
      cardBg: 'bg-[#fafaf5]',
      headerBg: 'bg-[#fafaf5]',
      contentBg: 'bg-[#fafaf5]',
      inputBg: 'bg-white',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      textMuted: 'text-gray-500',
      timestampColor: 'text-white',
      border: 'border-[#f6f6f0]',
      borderLight: 'border-[#f6f6f1]',
      hover: 'hover:bg-gray-100',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-gray-50',
      accent: 'bg-[#40B5AD]',
      accentHover: 'hover:bg-[#359691]',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    }
  };
  
  const currentTheme = isDarkMode ? theme.dark : theme.light;
  
  // Theme toggle function
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Graph-Chat resize handlers
  const handleGraphChatResizeMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    
    const container = document.querySelector('.graph-chat-container');
    if (!container) {
      console.log('Container not found!');
      return;
    }
    
    const containerRect = container.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;
    const containerWidth = containerRect.width;
    const percentage = (mouseX / containerWidth) * 100;
    
    // Constrain between 30% and 80%
    const newGraphWidth = Math.min(Math.max(percentage, 30), 80);
    console.log('Setting new width:', newGraphWidth);
    setGraphChatSplit(newGraphWidth);
  }, []);

  const handleGraphChatResizeEnd = useCallback(() => {
    console.log('Resize end');
    isResizingRef.current = false;
    setIsResizingGraphChat(false);
    document.removeEventListener('mousemove', handleGraphChatResizeMove);
    document.removeEventListener('mouseup', handleGraphChatResizeEnd);
  }, [handleGraphChatResizeMove]);

  const handleGraphChatResizeStart = useCallback((e) => {
    console.log('Resize start');
    e.preventDefault();
    isResizingRef.current = true;
    setIsResizingGraphChat(true);
    document.addEventListener('mousemove', handleGraphChatResizeMove);
    document.addEventListener('mouseup', handleGraphChatResizeEnd);
  }, [handleGraphChatResizeMove, handleGraphChatResizeEnd]);

  // Format timestamp function
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Parse the timestamp (handles both ISO strings and timestamps)
      const date = new Date(timestamp);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('[TIMESTAMP] Invalid timestamp:', timestamp);
        return 'Invalid time';
      }
      
      // Convert to user's local timezone and format
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      console.error('[TIMESTAMP] Error formatting timestamp:', timestamp, error);
      return 'Error';
    }
  };

  // Format bot messages for better readability
  const formatBotMessage = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    let formatted = text;
    
    // 1. Format numbered lists - more conservative approach
    // Only format when we have clear numbered sequences like "1. ... 2. ... 3. ..."
    formatted = formatted.replace(/(\d+\..*?)(\n\s*\n)(\d+\.\s)/gs, '$1<br/><br/>$3');

    
    // 2. Bold text: **text** → <strong>text</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    
    
    // 3. Color-coded severity levels with better styling
    formatted = formatted.replace(/\(Severity:\s*(Critical)\)/gi, '<span style="color: #dc2626; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(High)\)/gi, '<span style="color: #ea580c; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(Medium)\)/gi, '<span style="color: #d97706; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(Low)\)/gi, '<span style="color: #16a34a; font-weight: 600;">(Severity: $1)</span>');
    
    
    // 4. Add line breaks before standalone questions that start with capital letters
    formatted = formatted.replace(/([.!])\s+((?:Would you like|Please select|Do you want|Shall I|Would you prefer)[^?]*\?)/gi, '$1<br/><br/>$2');
    
    // 5. Preserve existing line breaks and convert them to HTML
    formatted = formatted.replace(/\n/g, '<br/>');
    
    // 6. Clean up multiple consecutive br tags
    formatted = formatted.replace(/(<br\/>){3,}/g, '<br/><br/>');
    
    return formatted;
  };

    // Function to extract user's traversal path using visitedNodes from escalation data
  const extractUserPath = (conversationHistory, graphData) => {
    if (!graphData || !Array.isArray(graphData) || graphData.length === 0) {
      return [];
    }

    console.log('[DEBUG] Extracting user path using visitedNodes');
    
    // Get visited nodes from escalated session data
    const sessionData = activeSession ? escalatedSessions.find(sess => sess.sessionId === activeSession) : null;
    const visitedNodeNames = sessionData?.visitedNodes || [];
    
    console.log('[DEBUG] Visited nodes from session:', visitedNodeNames);
    
    if (visitedNodeNames.length === 0) {
      console.log('[DEBUG] No visited nodes found, falling back to command matching');
      return [];
    }

    const nodes = graphData.filter(item => item.type === 'node');
    const traversedNodeIds = [];
    
    // Map visited node names to graph node IDs
    for (const visitedName of visitedNodeNames) {
      console.log('[DEBUG] Trying to match visited node:', visitedName);
      
      let matchingNode = null;
      
      // Check all nodes for exact name match first
      for (const node of nodes) {
        const nodeProps = node.data.properties || {};
        const nodeName = nodeProps.name || '';
        const nodeStepTitle = nodeProps.step_title || '';
        
        // Try matching against step_title first (for Graphiant nodes)
        if (nodeStepTitle === visitedName) {
          console.log('[DEBUG] ✅ Exact step_title match found:', nodeStepTitle);
          matchingNode = node;
          break;
        }
        
        // Try matching against name property
        if (nodeName === visitedName) {
          console.log('[DEBUG] ✅ Exact name match found:', nodeName);
          matchingNode = node;
          break;
        }
      }
      
      // If no exact match, try partial matching
      if (!matchingNode) {
        for (const node of nodes) {
          const nodeProps = node.data.properties || {};
          const nodeName = nodeProps.name || '';
          const nodeStepTitle = nodeProps.step_title || '';
          const nodeCommand = nodeProps.command || '';
          
          // Partial step_title match for Graphiant nodes
          if (nodeStepTitle && visitedName && (
              nodeStepTitle.toLowerCase().includes(visitedName.toLowerCase()) || 
              visitedName.toLowerCase().includes(nodeStepTitle.toLowerCase())
            )) {
            console.log('[DEBUG] ✅ Partial step_title match found:', nodeStepTitle);
            matchingNode = node;
            break;
          }
          
          // Partial name match for similar nodes
          if (nodeName && visitedName && (
              nodeName.toLowerCase().includes(visitedName.toLowerCase()) || 
              visitedName.toLowerCase().includes(nodeName.toLowerCase())
            )) {
            console.log('[DEBUG] ✅ Partial name match found:', nodeName);
            matchingNode = node;
            break;
          }
          
          // Command-based matching for server troubleshooting  
          if (nodeCommand && visitedName && (
              nodeCommand.toLowerCase().includes(visitedName.toLowerCase()) ||
              visitedName.toLowerCase().includes(nodeCommand.toLowerCase())
            )) {
            console.log('[DEBUG] ✅ Command-based match found:', nodeName);
            matchingNode = node;
            break;
          }
        }
      }
      
      if (matchingNode && !traversedNodeIds.includes(matchingNode.data.id)) {
        traversedNodeIds.push(matchingNode.data.id);
        console.log('[DEBUG] ✅ Mapped visited node:', visitedName, '→', matchingNode.data.properties?.name, 'ID:', matchingNode.data.id);
      } else if (matchingNode) {
        console.log('[DEBUG] ⚠️ Node already in path:', visitedName, '→', matchingNode.data.properties?.name);
      } else {
        console.log('[DEBUG] ❌ Could not map visited node:', visitedName);
        // Debug: show all available node names and step_titles
        console.log('[DEBUG] Available nodes:', nodes.map(n => {
          const props = n.data.properties || {};
          return {
            name: props.name || 'unnamed',
            step_title: props.step_title || 'no-step-title'
          };
        }));
      }
    }

    console.log('[DEBUG] Final user path from visitedNodes:', traversedNodeIds.length, 'nodes');
    return traversedNodeIds;
  };


  // Function to parse commands from conversation history
  const parseCommandsFromHistory = (conversationHistory) => {
    const commands = [];

    console.log('[DEBUG] Parsing conversation history:', conversationHistory.length, 'messages');

    // STRATEGY 1: Look for bulk auto-triage message first (Network commands)
    const bulkMessage = conversationHistory.find(msg => 
      msg.sender === "user" && 
      msg.text && 
      msg.text.length > 1000 && 
      msg.text.includes("Router#") && 
      (msg.text.includes("show processes cpu") || msg.text.includes("show interfaces"))
    );

    if (bulkMessage) {
      console.log('[DEBUG] 🎯 Found bulk auto-triage message, extracting commands');
      const bulkCommands = extractCommandsFromBulkMessage(bulkMessage.text);
      commands.push(...bulkCommands);
      console.log('[DEBUG] ✅ Extracted', bulkCommands.length, 'commands from bulk message');
    }

    // STRATEGY 2: Look for server/graphiant step-by-step format
    const stepCommands = extractServerGraphiantCommands(conversationHistory);
    if (stepCommands.length > 0) {
      console.log('[DEBUG] 🎯 Found server/graphiant step format, extracting commands');
      commands.push(...stepCommands);
      console.log('[DEBUG] ✅ Extracted', stepCommands.length, 'commands from step format');
    }

    // STRATEGY 3: Look for bot command prompts with user responses
    const botCommandPrompts = extractBotCommandPrompts(conversationHistory);
    if (botCommandPrompts.length > 0) {
      console.log('[DEBUG] 🎯 Found bot command prompts, extracting commands');
      commands.push(...botCommandPrompts);
      console.log('[DEBUG] ✅ Extracted', botCommandPrompts.length, 'commands from bot prompts');
    }

    // STRATEGY 4: Look for individual command-output pairs (manual triage or individual responses)
    for (let i = 0; i < conversationHistory.length; i++) {
      const msg = conversationHistory[i];
      
      // Skip if this is the bulk message we already processed
      if (bulkMessage && msg === bulkMessage) continue;
      
      // Look for user messages that look like individual command outputs
      if (msg.sender === "user" && msg.text && msg.text.length > 50) {
        const commandFromOutput = identifyCommandFromOutput(msg.text);
        if (commandFromOutput) {
          console.log('[DEBUG] 🔍 Found individual command output for:', commandFromOutput);
          
          // Check if we already have this command from previous strategies
          const alreadyExists = commands.some(cmd => 
            cmd.command.toLowerCase().includes(commandFromOutput.toLowerCase()) ||
            commandFromOutput.toLowerCase().includes(cmd.command.toLowerCase())
          );
          
          if (!alreadyExists) {
            commands.push({
              id: `cmd_individual_${i}`,
              command: commandFromOutput,
              botMessage: `Command: ${commandFromOutput}`,
              timestamp: Date.now(),
              output: msg.text
            });
            console.log('[DEBUG] ✅ Added individual command:', commandFromOutput);
          } else {
            console.log('[DEBUG] ⚠️  Command already exists from previous strategies, skipping:', commandFromOutput);
          }
        }
      }
    }

    console.log('[DEBUG] Final parsed commands:', commands.length);
    commands.forEach((cmd, idx) => {
      console.log(`[DEBUG] Command ${idx + 1}:`, cmd.command, '→', cmd.output ? cmd.output.substring(0, 30) + '...' : 'NO OUTPUT');
    });

    // Remove duplicate commands (same command text)
    const uniqueCommands = [];
    const seenCommands = new Set();
    
    for (const cmd of commands) {
      const normalizedCommand = cmd.command.toLowerCase().trim();
      if (!seenCommands.has(normalizedCommand)) {
        seenCommands.add(normalizedCommand);
        uniqueCommands.push(cmd);
      }
    }
    
    console.log('[DEBUG] After deduplication:', uniqueCommands.length, 'unique commands');
    return uniqueCommands;
  };

  // Extract commands from bulk auto-triage message
  const extractCommandsFromBulkMessage = (bulkText) => {
    const commands = [];
    const lines = bulkText.split('\n');
    
    console.log('[DEBUG] 📝 Processing bulk message with', lines.length, 'lines');
    
    // Define the commands we're looking for
    const targetCommands = [
      'show version',
      'show processes cpu',
      'show interfaces | include errors|broadcast',
      'show mac address-table dynamic',
      'show bgp summary | include Active|Idle'
    ];
    
    for (const targetCmd of targetCommands) {
      const output = extractSpecificCommandOutput(bulkText, targetCmd);
      if (output) {
        commands.push({
          id: `bulk_cmd_${commands.length}`,
          command: targetCmd,
          botMessage: `Auto-triage command: ${targetCmd}`,
          timestamp: Date.now(),
          output: output
        });
        console.log('[DEBUG] ✅ Extracted from bulk:', targetCmd, 'output length:', output.length);
      } else {
        console.log('[DEBUG] ❌ Could not extract from bulk:', targetCmd);
      }
    }
    
    return commands;
  };

  // Extract commands from server/graphiant step-by-step format
  const extractServerGraphiantCommands = (conversationHistory) => {
    const commands = [];
    
    // Combine all user messages to look for the step-by-step pattern
    const allUserText = conversationHistory
      .filter(msg => msg.sender === "user")
      .map(msg => msg.text)
      .join('\n\n');
    
    console.log('[DEBUG] 📝 Processing server/graphiant step format, combined text length:', allUserText.length);
    
    // Look for patterns like:
    // Step: [Step Name]
    // Command to run: [command]
    // Paste command output: [output]
    // Agent Analysis: [analysis]
    
    const stepPattern = /Step:\s*([^\n]+)\n[\s\S]*?Command to run:\s*([^\n]+)\n[\s\S]*?Paste command output:\s*([\s\S]*?)(?=\n\nAgent Analysis:|Step:|$)/gi;
    
    let match;
    let cmdIndex = 0;
    
    while ((match = stepPattern.exec(allUserText)) !== null) {
      const stepName = match[1].trim();
      const command = match[2].trim();
      const output = match[3].trim();
      
      console.log('[DEBUG] 🔍 Found server/graphiant step:', stepName);
      console.log('[DEBUG] 🔍 Command:', command);
      console.log('[DEBUG] 🔍 Output length:', output.length);
      
      if (command && output) {
        commands.push({
          id: `step_cmd_${cmdIndex}`,
          command: command,
          botMessage: `${stepName}: ${command}`,
          timestamp: Date.now(),
          output: output,
          stepName: stepName
        });
        console.log('[DEBUG] ✅ Added server/graphiant command:', command);
        cmdIndex++;
      }
    }
    
    // Fallback: Look for individual "Command to run:" patterns in separate messages
    if (commands.length === 0) {
      console.log('[DEBUG] 🔄 No step pattern found, trying individual message parsing');
      
      for (let i = 0; i < conversationHistory.length; i++) {
        const msg = conversationHistory[i];
        
        if (msg.sender === "user" && msg.text) {
          // Look for "Command to run:" pattern
          const commandMatch = msg.text.match(/Command to run:\s*([^\n]+)/i);
          if (commandMatch) {
            const command = commandMatch[1].trim();
            
            // Look for the output in the next few user messages
            let output = '';
            let stepName = '';
            
            // Try to find step name in the same message
            const stepMatch = msg.text.match(/Step:\s*([^\n]+)/i);
            if (stepMatch) {
              stepName = stepMatch[1].trim();
            }
            
            for (let j = i + 1; j < Math.min(i + 5, conversationHistory.length); j++) {
              const nextMsg = conversationHistory[j];
              if (nextMsg.sender === "user" && nextMsg.text) {
                if (nextMsg.text.includes("Paste command output:")) {
                  output = nextMsg.text.replace(/.*Paste command output:\s*/i, '').trim();
                  break;
                } else if (nextMsg.text.length > 50 && !nextMsg.text.includes("Command to run:")) {
                  // Assume this is the output if it's substantial and not another command
                  output = nextMsg.text.trim();
                  break;
                }
              }
            }
            
            if (output) {
              commands.push({
                id: `individual_step_cmd_${cmdIndex}`,
                command: command,
                botMessage: stepName ? `${stepName}: ${command}` : `Command: ${command}`,
                timestamp: Date.now(),
                output: output,
                stepName: stepName || undefined
              });
              console.log('[DEBUG] ✅ Added individual server/graphiant command:', command);
              cmdIndex++;
            }
          }
        }
      }
    }
    
    return commands;
  };

  // Extract commands from bot command prompts with user responses
  const extractBotCommandPrompts = (conversationHistory) => {
    const commands = [];
    
    for (let i = 0; i < conversationHistory.length; i++) {
      const msg = conversationHistory[i];
      
      // Look for bot messages that contain commands
      if (msg.sender === "bot" && msg.text) {
        const text = msg.text.toLowerCase();
        let commandText = null;
        let isGraphiantCommand = false;

        // === GRAPHIANT COMMANDS ===
        // Check for specific graphiant diagnostic commands
        if (text.includes("ping -c 4") || text.includes("get /network/routing?prefix")) {
          // For graphiant, the command is in the bot message itself
          commandText = text.trim();
          isGraphiantCommand = true;
          
          // Look for the next bot message as the response
          const nextMsg = conversationHistory[i + 1];
          if (nextMsg && nextMsg.sender === "bot") {
            commands.push({
              id: `graphiant_cmd_${i}`,
              command: commandText,
              botMessage: nextMsg.text,
              timestamp: Date.now(),
              output: nextMsg.text
            });
            
            const cmdType = text.includes("ping -c 4") ? "ping" : "routing";
            console.log(`[DEBUG] ✅ Added graphiant diagnostic pair for ${cmdType}:`, nextMsg.text.substring(0, 50));
            continue;
          }
        }
        
        // === SERVER/NETWORK COMMANDS ===
        // Look for command indicators
        if (!isGraphiantCommand && (text.includes("execute") || text.includes("run") || text.includes("command") || 
            text.includes("please execute the following command") || text.includes("paste the output") ||
            text.includes("could you run") || text.includes("share the output"))) {
          
          // SERVER PATTERN: "Please execute the following command and paste the output: commandhere"
          const serverPattern = /please execute the following command and paste (?:the )?output:\s*(.+)/i;
          const serverMatch = msg.text.match(serverPattern);
          if (serverMatch) {
            commandText = serverMatch[1].trim();
          }
          
          // NETWORK PATTERN: First try to find code blocks or backticks
          if (!commandText) {
            const codeBlockMatch = msg.text.match(/```([^`]+)```/) || msg.text.match(/`([^`]+)`/);
            if (codeBlockMatch) {
              commandText = codeBlockMatch[1].trim();
            }
          }
          
          // NETWORK PATTERN: If no code block, look for common network command patterns
          if (!commandText) {
            const networkCommandPatterns = [
              /\b(show\s+ip\s+interface\s+brief)\b/i,
              /\b(show\s+interfaces?\s+brief)\b/i,
              /\b(show\s+ip\s+route)\b/i,
              /\b(show\s+running-config)\b/i,
              /\b(show\s+processes\s+cpu)\b/i,
              /\b(show\s+version)\b/i,
              /\b(ping\s+[\d\.]+)\b/i,
              /\b(traceroute\s+[\d\.]+)\b/i,
              /\b(show\s+\w+(?:\s+\w+)*)\b/i
            ];
            
            for (const pattern of networkCommandPatterns) {
              const match = msg.text.match(pattern);
              if (match) {
                commandText = match[1].trim();
                break;
              }
            }
          }
          
          // SERVER PATTERN: Look for common server command patterns
          if (!commandText) {
            const serverCommandPatterns = [
              /\b(top\s*(?:-\w+)*)\b/i,
              /\b(ps\s+[\w\s\-]+)\b/i,
              /\b(netstat\s+[\w\s\-\|]+)\b/i,
              /\b(systemctl\s+\w+\s+\w+)\b/i,
              /\b(df\s*(?:-\w+)*)\b/i,
              /\b(free\s*(?:-\w+)*)\b/i,
              /\b(htop)\b/i,
              /\b(kill\s+[\w\s\-]+)\b/i,
              /\b(lsof\s+[\w\s\-]+)\b/i,
              /\b(md5sum\s+[\w\s\/\-]+)\b/i,
              /\b(crontab\s+[\w\s\-]+)\b/i,
              /\b(iptables\s+[\w\s\-]+)\b/i,
              /\b(rm\s+[\w\s\-\/]+)\b/i,
              /\b(apt-get\s+[\w\s\-]+)\b/i
            ];
            
            for (const pattern of serverCommandPatterns) {
              const match = msg.text.match(pattern);
              if (match) {
                commandText = match[1].trim();
                break;
              }
            }
          }

          if (commandText) {
            console.log('[DEBUG] Found bot command prompt:', commandText, 'at message index:', i);
            
            // Look for the next user message as the output
            for (let j = i + 1; j < conversationHistory.length; j++) {
              const nextMsg = conversationHistory[j];
              if (nextMsg.sender === "user") {
                const command = {
                  id: `bot_prompt_cmd_${i}`,
                  command: commandText,
                  botMessage: msg.text,
                  timestamp: Date.now(),
                  output: nextMsg.text
                };
                commands.push(command);
                console.log('[DEBUG] ✅ Paired bot command prompt:', commandText, 'with next user message:', nextMsg.text.substring(0, 50), '...');
                break; // Found the pair, move to next command
              }
            }
          }
        }
      }
    }
    
    return commands;
  };

  // Extract specific command output from bulk text
  const extractSpecificCommandOutput = (bulkText, targetCommand) => {
    const lines = bulkText.split('\n');
    let startIndex = -1;
    let endIndex = -1;
    
    // Find the command line
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('Router#') && line.toLowerCase().includes(targetCommand.toLowerCase())) {
        startIndex = i + 1; // Start after the command line
        console.log('[DEBUG] 📍 Found command at line', i, ':', line.substring(0, 50));
        break;
      }
    }
    
    if (startIndex === -1) return null;
    
    // Find the end (next Router# command or specific patterns)
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i];
      
      // End conditions
      if (i > startIndex && line.includes('Router#')) {
        endIndex = i;
        break;
      }
      
      // Special case for BGP summary - look for neighbor table start
      if (targetCommand.includes('bgp summary') && line.includes('Neighbor') && line.includes('AS')) {
        // Include the header and a few more lines
        for (let j = i; j < Math.min(i + 10, lines.length); j++) {
          if (j > i && lines[j].trim() === '') {
            endIndex = j;
            break;
          }
        }
        if (endIndex === -1) endIndex = Math.min(i + 5, lines.length);
        break;
      }
    }
    
    if (endIndex === -1) endIndex = lines.length;
    
    const outputLines = lines.slice(startIndex, endIndex);
    const output = outputLines.join('\n').trim();
    
    console.log('[DEBUG] ✂️ Extracted lines', startIndex, 'to', endIndex, 'for', targetCommand);
    return output;
  };

  // Identify command from output text (for individual messages)
  const identifyCommandFromOutput = (outputText) => {
    if (!outputText) return null;
    
    const text = outputText.toLowerCase();
    
    // ========== NETWORK COMMANDS ==========
    // CPU output patterns
    if (text.includes('cpu utilization') || text.includes('pid') && text.includes('runtime')) {
      return 'show processes cpu';
    }
    
    // Interface output patterns  
    if (text.includes('gigabitethernet') && (text.includes('input errors') || text.includes('broadcast'))) {
      return 'show interfaces | include errors|broadcast';
    }
    
    // MAC table output patterns
    if (text.includes('mac address table') || (text.includes('vlan') && text.includes('dynamic'))) {
      return 'show mac address-table dynamic';
    }
    
    // BGP output patterns
    if (text.includes('neighbor') && text.includes('msgrcvd') || text.includes('active') && text.includes('idle')) {
      return 'show bgp summary | include Active|Idle';
    }
    
    // BGP neighbors output
    if (text.includes('bgp state') || text.includes('neighbor') && text.includes('established')) {
      return 'show bgp neighbors | include state';
    }
    
    // Version output patterns
    if (text.includes('version') && (text.includes('ios') || text.includes('software') || text.match(/version\s+\d+\.\d+/))) {
      return 'show version';
    }
    
    // ========== SERVER COMMANDS ==========
    // top command output
    if (text.includes('load average') && text.includes('tasks:') && text.includes('%cpu')) {
      return 'top -c';
    }
    
    // ps command output
    if (text.includes('uid') && text.includes('ppid') && text.includes('cmd')) {
      return 'ps -fp <PID>';
    }
    
    // netstat command output
    if (text.includes('tcp') && text.includes('established') && text.includes('0.0.0.0')) {
      return 'netstat -tulpn | grep <PID>';
    }
    
    // lsof command output
    if (text.includes('command') && text.includes('user') && text.includes('type') && text.includes('device')) {
      return 'lsof -p <PID>';
    }
    
    // md5sum command output
    if (text.match(/[a-f0-9]{32}/i) && text.includes('/proc/')) {
      return 'md5sum /proc/<PID>/exe';
    }
    
    // crontab/systemctl output
    if (text.includes('mailto') && text.includes('cron') || text.includes('systemctl') && text.includes('timer')) {
      return 'crontab -l; systemctl list-timers';
    }
    
    // kill command output
    if (text.includes('pid') && text.includes('tty') && text.includes('time') && text.includes('cmd')) {
      return 'kill -9 <PID>';
    }
    
    // rm command output
    if (text.includes('cannot access') && text.includes('no such file or directory')) {
      return 'rm -rf /tmp/hidden';
    }
    
    // iptables command output
    if (text.includes('chain output') && text.includes('drop') && text.includes('policy accept')) {
      return 'iptables -A OUTPUT -d <IP> -j DROP';
    }
    
    // apt-get command output
    if (text.includes('listing') && text.includes('done') && text.includes('upgrade')) {
      return 'apt-get update && apt-get upgrade';
    }
    
    // df command output
    if (text.includes('filesystem') && text.includes('mounted on') && text.includes('available')) {
      return 'df -h';
    }
    
    // free command output
    if (text.includes('mem:') && text.includes('swap:') && text.includes('available')) {
      return 'free -h';
    }
    
    // systemctl command output
    if (text.includes('active') && text.includes('inactive') && text.includes('service')) {
      return 'systemctl status <service>';
    }
    
    // ========== GRAPHIANT COMMANDS ==========
    // Add graphiant command patterns here when we have examples
    // For now, check for common graphiant patterns
    if (text.includes('graphiant') || text.includes('edge') || text.includes('wan')) {
      // Generic graphiant command detection - might need refinement
      if (text.includes('status') || text.includes('config') || text.includes('route')) {
        return 'graphiant status/config command';
      }
    }
    
    // Ping output patterns (could be network or graphiant)
    if (text.includes('ping statistics') || text.includes('packets transmitted')) {
      return 'ping -c 4 <target>';
    }
    
    return null;
  };

  // Function to handle command click - modified to show popup
  const handleCommandClick = (command) => {
    if (!activeSession) return;
    setSelectedCommand(command);
    setShowCommandOutput(true);
  };

  // Function to handle node click
  const handleNodeClick = (nodeData) => {
    setSelectedNode(nodeData);
  };

  // Neo4j-style node details component
  const NodeDetailsPanel = ({ nodeData }) => {
    if (!nodeData) return null;

    const props = nodeData.properties || {};
    const labels = nodeData.labels || [];
    
    return (
      <div className={`${currentTheme.buttonBg} ${currentTheme.borderLight} border rounded-lg p-4 shadow-sm transition-colors duration-300`}>
        <div className="flex items-center mb-3">
          <div 
            className="w-6 h-6 rounded-full mr-3"
            style={{
              backgroundColor: nodeData.nodeType === 'current' ? '#C990C0' :
                             nodeData.nodeType === 'previous' ? '#4C8EDA' :
                             nodeData.nodeType === 'next' ? '#57C7E3' :
                             nodeData.nodeType === 'terminal' ? '#FF6B6B' :
                             nodeData.nodeType === 'tool' ? '#F79767' : '#569480'
            }}
          />
          <div>
            <h3 className={`font-semibold ${currentTheme.text}`}>
              {props.name || props.command || 'Node'}
            </h3>
            {labels.length > 0 && (
              <div className={`text-xs ${currentTheme.textSecondary}`}>
                {labels.map((label, idx) => (
                  <span key={idx} className={`inline-block ${currentTheme.buttonBg} px-2 py-1 rounded mr-1 ${currentTheme.textSecondary}`}>
                    :{label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          <div className={`text-sm font-medium ${currentTheme.text} ${currentTheme.border} border-b pb-1 mb-2`}>Properties</div>
          {Object.keys(props).length === 0 ? (
            <div className={`text-sm ${currentTheme.textSecondary} italic`}>No properties</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(props).map(([key, value]) => (
                <div key={key} className="mb-3">
                  <div className={`text-sm font-medium ${currentTheme.textSecondary} mb-1`}>
                    {key}:
                  </div>
                  <div className={`text-sm ${currentTheme.text} break-words overflow-wrap-anywhere leading-relaxed pl-2 border-l-2 ${currentTheme.border}`}>
                    {typeof value === 'string' && value.length > 100 
                      ? `${value.substring(0, 100)}...` 
                      : String(value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {nodeData.nodeType && (
          <div className={`mt-3 pt-2 ${currentTheme.border} border-t`}>
            <div className={`text-xs ${currentTheme.textSecondary}`}>
              Node Type: <span className={`font-medium ${currentTheme.text}`}>{nodeData.nodeType}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Legacy function - keeping for backward compatibility but not used with Vis-Network
  const renderGraph = (graphData) => {
    // This function is now replaced by VisNetworkGraphComponent
    return null;
  };

  // Helper function to get node colors based on status
  const getNodeColorByStatus = (status) => {
    switch (status.status) {
      case 'problematic':
        return {
          bg: 'bg-red-50',
          border: 'border-red-500',
          text: 'text-red-800',
          textLight: 'text-red-600'
        };
      case 'cleared':
        return {
          bg: 'bg-green-50',
          border: 'border-green-500',
          text: 'text-green-800',
          textLight: 'text-green-600'
        };
      case 'traversed':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-500',
          text: 'text-yellow-800',
          textLight: 'text-yellow-600'
        };
      case 'untraversed':
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-300',
          text: 'text-gray-700',
          textLight: 'text-gray-500'
        };
    }
  };

  useEffect(() => {
    const s = connectSocket();
    setSocket(s);

    // Join the 'agents' room for escalation alerts
    s.on("connect", () => {
      console.log("[AGENT-CONNECT-DEBUG] ========== AGENT CONNECTED ==========");
      console.log("[AGENT-CONNECT-DEBUG] Socket ID:", s.id);
      console.log("[AGENT-CONNECT-DEBUG] Attempting to join 'agents' room");
      
      s.emit("joinRoom", { sessionId: "agents", role: "agent" });
      
      console.log("[AGENT-CONNECT-DEBUG] Join request sent to server");
      console.log("[AGENT-CONNECT-DEBUG] ========== AGENT CONNECT COMPLETED ==========");
    });

    // Listen for escalation alerts
    s.on("escalationAlert", (data) => {
      console.log("[ESCALATION-ALERT-DEBUG] ========== ESCALATION ALERT RECEIVED ==========");
      console.log("[ESCALATION-ALERT-DEBUG] Alert data:", data);
      console.log("[ESCALATION-ALERT-DEBUG] Session ID:", data.sessionId);
      console.log("[ESCALATION-ALERT-DEBUG] User Info:", data.userInfo);
      console.log("[ESCALATION-ALERT-DEBUG] Escalation Time:", data.escalationTime);
      console.log("[ESCALATION-ALERT-DEBUG] Issue Type:", data.issueType);
      console.log("[ESCALATION-ALERT-DEBUG] Issue Name:", data.issueName);
      console.log("[ESCALATION-ALERT-DEBUG] Conversation History Length:", data.conversationHistory?.length || 0);
      console.log("[ESCALATION-ALERT-DEBUG] Visited Nodes:", data.visitedNodes);
      console.log("[ESCALATION-ALERT-DEBUG] User Commands:", data.userCommands?.length || 0, "commands");
      if (data.userCommands && data.userCommands.length > 0) {
        data.userCommands.forEach((cmd, idx) => {
          console.log(`[ESCALATION-ALERT-DEBUG] Command ${idx + 1}:`, cmd.command, '-> Output:', cmd.output ? cmd.output.substring(0, 50) + '...' : 'NO OUTPUT');
        });
      }
      
      setEscalatedSessions((prev) => {
        const exists = prev.some((sess) => sess.sessionId === data.sessionId);
        console.log("[ESCALATION-ALERT-DEBUG] Session already exists:", exists);
        
        if (exists) {
          console.log("[ESCALATION-ALERT-DEBUG] Ignoring duplicate escalation alert");
          return prev;
        }
        
        // Add escalation timestamp if not present (use UTC)
        const escalationData = {
          ...data,
          escalationTime: data.escalationTime || Date.now(),
          visitedNodes: data.visitedNodes || [],  // Store visited nodes from escalation
          userCommands: data.userCommands || []   // Store user commands from escalation
        };
        
        console.log("[ESCALATION-ALERT-DEBUG] Adding new escalated session:", escalationData);
        console.log("[ESCALATION-ALERT-DEBUG] ========== ESCALATION ALERT PROCESSED ==========");
        
        return [...prev, escalationData];
      });
    });

    // Receive full chat history when joining a session
    s.on("conversationHistory", (historyArr) => {
      setMessages(historyArr);
      // Only parse commands from conversation history if we don't already have escalation commands
      setUserCommands(prevCommands => {
        if (hasEscalationCommandsRef.current) {
          console.log('[DEBUG] Keeping existing user commands from escalation data (hasEscalationCommands=true)');
          return prevCommands; // Keep escalation commands
        }
        console.log('[DEBUG] Parsing commands from conversation history (hasEscalationCommands=false)');
        return parseCommandsFromHistory(historyArr);
      });
    });

    // Listen for new chat messages (from user, bot, or agent)
    s.on("chatMessage", (msg) => {
      // Filter out echo messages from the server to prevent duplicates
      // Echo messages have sender: "agent" and are sent back to the agent
      if (msg.sender === "agent") {
        console.log('[DEBUG] Ignoring echo message from server to prevent duplicate');
        return;
      }
      
      setMessages((prev) => {
        const newMessages = [...prev, { text: msg.text, sender: msg.sender || "bot" }];
        // Only update commands from new messages if we don't have escalation commands
        setUserCommands(prevCommands => {
          if (hasEscalationCommandsRef.current) {
            console.log('[DEBUG] Keeping existing user commands from escalation data (chatMessage, hasEscalationCommands=true)');
            return prevCommands; // Keep escalation commands
          }
          console.log('[DEBUG] Parsing commands from new chat message (hasEscalationCommands=false)');
          return parseCommandsFromHistory(newMessages);
        });
        return newMessages;
      });
    });

    // Listen for command graph data

    // s.on("commandGraphData", (data) => {
    //   // Clear any pending timeout
    //   if (window.currentGraphTimeout) {
    //     clearTimeout(window.currentGraphTimeout);
    //     window.currentGraphTimeout = null;
    //   }
      
    //   setGraphLoading(false);
    //   if (data.error) {
    //     console.error("Error fetching graph data:", data.error);
    //     setGraphData([]); // Use empty array as fallback
    //   } else {
    //     console.log('[DEBUG] Received graph data:', data.graphData?.length || 0, 'items');
    //     setGraphData(data.graphData || []);
    //   }
    // });

    // Listen for subgraph data
    s.on("subgraphData", (data) => {
      // Clear any pending timeout
      if (window.currentGraphTimeout) {
        clearTimeout(window.currentGraphTimeout);
        window.currentGraphTimeout = null;
      }

      setGraphLoading(false);
      if (data.error) {
        console.error("Error fetching subgraph data:", data.error);
        setGraphData([]); // Use empty array as fallback
      } else {
        console.log('[DEBUG] Received subgraph data:', data.graphData?.length || 0, 'items');
        console.log('[DEBUG] Received visited nodes:', data.visitedNodes?.length || 0, 'nodes');
        console.log('[DEBUG] Visited nodes list:', data.visitedNodes);
        console.log('[DEBUG] Sample graph data:', data.graphData?.slice(0, 3));
        setGraphData(data.graphData || []);
        
        // Update escalated session with visitedNodes if we have them
        if (data.visitedNodes && activeSession) {
          setEscalatedSessions(prev => prev.map(sess => 
            sess.sessionId === activeSession 
              ? { ...sess, visitedNodes: data.visitedNodes }
              : sess
          ));
        }
      }
    });

    // Listen for visited nodes data
    s.on("visitedNodesData", (data) => {
      if (data.error) {
        console.error("Error fetching visited nodes:", data.error);
      } else {
        console.log('[DEBUG] Received visited nodes data:', data.visitedNodes?.length || 0, 'nodes');
        
        // Update escalated session with visitedNodes
        if (data.visitedNodes && data.sessionId) {
          setEscalatedSessions(prev => prev.map(sess => 
            sess.sessionId === data.sessionId 
              ? { ...sess, visitedNodes: data.visitedNodes }
              : sess
          ));
        }
      }
    });

    // Listen for conversation summary
    s.on("conversationSummary", (data) => {
      setSummaryLoading(false);
      if (data.error) {
        console.error("Error generating summary:", data.error);
        setConversationSummary("Error generating summary. Please try again.");
      } else {
        console.log('[DEBUG] Received conversation summary:', data.summary);
        setConversationSummary(data.summary);
      }
    });

    // Listen for AI graph analysis
    s.on("graphAnalysis", (data) => {
      setGraphSummaryLoading(false);
      if (data.error) {
        console.error("Error generating graph analysis:", data.error);
        setAiGraphAnalysis("Error generating AI analysis. Please try again.");
      } else {
        console.log('[DEBUG] Received graph analysis:', data.analysis);
        setAiGraphAnalysis(data.analysis);
      }
    });

    return () => s.disconnect();
  }, []);

  // Cleanup resize event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleGraphChatResizeMove);
      document.removeEventListener('mouseup', handleGraphChatResizeEnd);
    };
  }, [handleGraphChatResizeMove, handleGraphChatResizeEnd]);

  useEffect(() => {
    chatBodyRef.current?.scrollTo({
      top: chatBodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  // Add this at the top of the file or inside the component to get the agent's username
  const agentName = localStorage.getItem("username") || "Agent";

  const handleJoinSession = (sessionId) => {
    console.log('[JOIN-SESSION-DEBUG] ========== JOINING SESSION ==========');
    console.log('[JOIN-SESSION-DEBUG] Session ID:', sessionId);
    console.log('[JOIN-SESSION-DEBUG] Available escalated sessions:', escalatedSessions.length);
    
    setActiveSession(sessionId);
    // Find and store the user info for this session
    const sessionData = escalatedSessions.find(sess => sess.sessionId === sessionId);
    console.log('[JOIN-SESSION-DEBUG] Found session data:', !!sessionData);
    
    if (sessionData) {
      console.log('[JOIN-SESSION-DEBUG] Session data keys:', Object.keys(sessionData));
      console.log('[JOIN-SESSION-DEBUG] User commands in session:', sessionData.userCommands?.length || 0);
      if (sessionData.userCommands) {
        console.log('[JOIN-SESSION-DEBUG] User commands details:', sessionData.userCommands);
      }
    }
    
    setActiveSessionUserInfo(sessionData?.userInfo || null);
    setMessages([]);
    
    // Load user commands from escalation data if available
    if (sessionData?.userCommands && Array.isArray(sessionData.userCommands) && sessionData.userCommands.length > 0) {
      console.log('[JOIN-SESSION-DEBUG] Loading user commands from escalation data:', sessionData.userCommands.length, 'commands');
      setUserCommands(sessionData.userCommands);
      setHasEscalationCommands(true);
    } else {
      console.log('[JOIN-SESSION-DEBUG] No user commands found in escalation data');
      setUserCommands([]); // Clear commands when switching sessions if no escalation data
      setHasEscalationCommands(false);
    }
    setSelectedCommand(null); // Clear selected command when switching sessions
    
    console.log('[JOIN-SESSION-DEBUG] ========== SESSION JOIN COMPLETE ==========');
    
    // Immediately fetch and display graph for this session
    setGraphLoading(true);
    setGraphData(null);
    
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.log('[DEBUG] Graph loading timeout, using fallback');
      setGraphLoading(false);
      setGraphData([]); // Empty array will trigger fallback in render function
    }, 5000); // 5 second timeout
    
    // Fetch subgraph for this session
    socket.emit('getSubgraph', {
      sessionId: sessionId
    });
    
    // Also request visited nodes as a fallback
    socket.emit('getVisitedNodes', {
      sessionId: sessionId
    });
    
    // Store timeout reference for cleanup
    window.currentGraphTimeout = timeoutId;
    
    socket.emit("liveAgentJoin", { user_sid: sessionId, agent_name: agentName });
    
    console.log('[DEBUG] Session selected, fetching graph data and visited nodes for session:', sessionId);
  };

  const sendMessage = () => {
    if (!message.trim() || !activeSession) return;
    
    // Add the message to local state immediately so agent sees it
    const agentMessage = {
      text: message,
      sender: "agent",
      timestamp: Date.now()
    };
    
    setMessages((prev) => [...prev, agentMessage]);
    
    // Send to server
    socket.emit("liveAgentMessage", {
      user_sid: activeSession,
      text: message,
      agent_name: agentName,
    });
    setMessage("");
  };

  // Function to determine node status based on conversation history and command outputs
  const analyzeNodeStatus = (node, conversationHistory, userCommands) => {
    try {
      if (!node || !node.properties) {
        return { status: 'untraversed', color: 'white', textColor: 'gray' };
      }
      
      const nodeCommand = node.properties.command;
      
      if (!nodeCommand || !Array.isArray(userCommands)) {
        return { status: 'untraversed', color: 'white', textColor: 'gray' };
      }
      
      // Check if this node's command was executed
      const executedCommand = userCommands.find(cmd => {
        if (!cmd || !cmd.command) return false;
        const cmdLower = cmd.command.toLowerCase();
        const nodeCmdLower = nodeCommand.toLowerCase();
        
        // More flexible matching
        return cmdLower.includes(nodeCmdLower.split(' ')[0]) || 
               nodeCmdLower.includes(cmdLower.split(' ')[0]);
      });
      
      if (!executedCommand) {
        return { status: 'untraversed', color: 'white', textColor: 'gray' };
      }
    
    // Analyze command output to determine if this step identified an issue
    const output = executedCommand.output || '';
    const command = nodeCommand || '';
    
    // Simple heuristics for common network commands
    if (command.includes('show processes cpu')) {
      const cpuMatch = output.match(/(\d+)%/);
      const cpuUsage = cpuMatch ? parseInt(cpuMatch[1]) : 0;
      if (cpuUsage >= 90) {
        return { status: 'problematic', color: 'red', textColor: 'red', analysis: `High CPU usage detected: ${cpuUsage}%` };
      } else {
        return { status: 'cleared', color: 'green', textColor: 'green', analysis: `CPU usage normal: ${cpuUsage}%` };
      }
    }
    
    if (command.includes('show interfaces') && command.includes('errors')) {
      const errorLines = output.split('\n').filter(line => 
        line.includes('errors') || line.includes('drops') || line.includes('collisions')
      );
      const hasErrors = errorLines.some(line => {
        const numbers = line.match(/\d+/g) || [];
        return numbers.some(num => parseInt(num) > 0);
      });
      
      if (hasErrors) {
        return { status: 'problematic', color: 'red', textColor: 'red', analysis: 'Interface errors detected' };
      } else {
        return { status: 'cleared', color: 'green', textColor: 'green', analysis: 'No interface errors found' };
      }
    }
    
    if (command.includes('show mac address-table')) {
      const macCount = (output.match(/\d+\.\d+\.\d+/g) || []).length;
      if (macCount > 1000) {
        return { status: 'problematic', color: 'red', textColor: 'red', analysis: `High MAC count: ${macCount} entries` };
      } else {
        return { status: 'cleared', color: 'green', textColor: 'green', analysis: `Normal MAC count: ${macCount} entries` };
      }
    }
    
    // Default: traversed but unclear result
    return { status: 'traversed', color: 'yellow', textColor: 'orange', analysis: 'Command executed - manual review needed' };
    } catch (error) {
      console.error('Error in analyzeNodeStatus:', error);
      return { status: 'untraversed', color: 'white', textColor: 'gray', analysis: 'Error analyzing status' };
    }
  };

  // Function to render indented view
  const renderIndentedView = () => {
    const sessionData = activeSession ? 
      escalatedSessions.find(sess => sess.sessionId === activeSession) : null;
    const visitedNodes = sessionData?.visitedNodes || [];

    if (!visitedNodes.length) {
      return (
        <div className={`${currentTheme.textSecondary} text-center py-8`}>
          No steps data available
        </div>
      );
    }

    return (
      <div className="p-4 font-mono text-base">
        {visitedNodes.map((node, index) => (
          <div key={index} className={`${currentTheme.text}`}>
            {'    '.repeat(index)}{index > 0 ? '⤷ ' : ''}
            <span className={index === 0 ? 'font-bold' : ''}>
              {node}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Graph Legend Component
  const GraphLegend = () => {
    const legendItems = [
      { color: '#8B5CF6', label: 'Root Node (Start)', type: 'node'  },
      { color: '#90d5ff', label: 'Diagnosis Step', type: 'node'  },
      { color: '#F59E0B', label: 'Recommendation Step', type: 'node'  },
      { color: '#E5E7EB', label: 'Workflow Step', type: 'node'  },
      // { color: '#FF6B6B', label: 'Terminal/Escalation', type: 'node' },
    
      { color: 'transparent', label: 'Traversed Node', type: 'border', border: '#10B981' },
      { color: 'transparent', label: 'Non-traversed Border', type: 'border', border: '#EF4444' },
      { color: '#10B981', label: 'User Path Edge', type: 'edge' },
      
    ];

    return (
      <div className={`${currentTheme.cardBg} ${currentTheme.border} border rounded-lg p-3 mt-3 transition-colors duration-300`}>
        <h3 className={`text-sm font-semibold ${currentTheme.text} mb-3`}>Graph Legend</h3>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {legendItems.map((item, index) => (
            <div key={index} className="flex items-center">
              {item.type === 'node' ? (
                <div 
                  className="w-4 h-4 rounded-full mr-2 border-2"
                  style={{ 
                    backgroundColor: item.color,
                    borderColor: item.border || item.color
                  }}
                />
              ) : item.type === 'border' ? (
                <div 
                  className="w-4 h-4 rounded-full mr-2 border-2"
                  style={{ 
                    backgroundColor: 'transparent',
                    borderColor: item.border
                  }}
                />
              ) : (
                <div className="flex items-center mr-2">
                  <div 
                    className="w-4 h-0.5"
                    style={{ 
                      backgroundColor: item.color,
                      border: item.label === 'Uses Tool' ? '1px dashed' : 'none'
                    }}
                  />
                  <div 
                    className="w-0 h-0 ml-1"
                    style={{
                      borderLeft: `3px solid ${item.color}`,
                      borderTop: '2px solid transparent',
                      borderBottom: '2px solid transparent'
                    }}
                  />
                </div>
              )}
              <span className={`${currentTheme.textSecondary}`}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Function to request conversation summary
  const requestConversationSummary = () => {
    if (!activeSession) return;
    
    setSummaryLoading(true);
    setShowSummary(true);
    setConversationSummary('');
    
    socket.emit('getConversationSummary', {
      sessionId: activeSession
    });
  };

  // Function to request AI graph analysis
  const requestGraphAnalysis = () => {
    if (!activeSession) return;
    
    setGraphSummaryLoading(true);
    setShowGraphSummary(true);
    setAiGraphAnalysis('');
    
    const sessionData = escalatedSessions.find(sess => sess.sessionId === activeSession);
    const visitedNodes = sessionData?.visitedNodes || [];
    const userCommands = sessionData?.userCommands || [];
    
    socket.emit('getGraphAnalysis', {
      sessionId: activeSession,
      visitedNodes: visitedNodes,
      userCommands: userCommands
    });
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .scrollbar-hide {
            -ms-overflow-style: none;  /* Internet Explorer 10+ */
            scrollbar-width: none;  /* Firefox */
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;  /* Safari and Chrome */
          }
          .command-popup {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 50;
            max-width: 90vw;
            width: 600px;
            max-height: 80vh;
          }
          .popup-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 40;
          }
          .graph-chat-resize-handle {
            width: 4px;
            background-color: transparent;
            cursor: col-resize;
            position: relative;
            z-index: 10;
            transition: background-color 0.2s ease;
            user-select: none;
          }
          .graph-chat-resize-handle:hover {
            background-color: #06b6d4;
          }
          .graph-chat-resize-handle:active {
            background-color: #0891b2;
          }
        `
      }} />
      <div className={`flex min-h-screen h-screen w-full ${currentTheme.bg} overflow-hidden transition-colors duration-300 ${isResizingGraphChat ? 'select-none' : ''}`}>
      {/* Sidebar */}
      <SideBar
        mode="agent"
        onLogout={onLogout}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        onLinkClick={(path) => navigate(path)}
      />
      
      {/* Main Content Area - 3 Columns Layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left Column - Escalated Sessions (top) + User Commands (bottom) */}
        <div className={`w-1/6 flex flex-col ${currentTheme.border} border-r`}>
          {/* Escalated Sessions - Top Half of Left Column */}
          <div className={`h-1/2 ${currentTheme.cardBg} ${currentTheme.border} border-b p-2 overflow-y-auto scrollbar-hide transition-colors duration-300`}>
            <h2 className={`font-bold text-xs mb-1 ${currentTheme.text}`}>Escalated Sessions</h2>
            <ul>
              {escalatedSessions.length === 0 && (
                <li className={`${currentTheme.textSecondary} text-xs`}>No escalations yet</li>
              )}
              {escalatedSessions.map((sess) => (
                <li
                  key={sess.sessionId}
                  className={`p-1 rounded cursor-pointer mb-1 text-xs transition-colors duration-200 ${
                    activeSession === sess.sessionId
                      ? 'bg-[#06b6d4] font-semibold text-white'
                      : `${currentTheme.hover} ${currentTheme.textSecondary} hover:${currentTheme.text}`
                  }`}
                  onClick={() => handleJoinSession(sess.sessionId)}
                >
                  <div className="font-medium truncate">
                    {sess.userInfo && sess.userInfo.name
                      ? sess.userInfo.name
                      : sess.sessionId.slice(-6)}
                  </div>
                  {sess.escalationTime && (
                    <div className={`text-xs ${currentTheme.timestampColor} mt-1`}>
                      {formatTimestamp(sess.escalationTime)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* User Commands - Bottom Half of Left Column */}
          <div className={`h-1/2 ${currentTheme.cardBg} p-2 overflow-y-auto scrollbar-hide transition-colors duration-300`}>
            <h2 className={`font-bold text-xs mb-1 ${currentTheme.text}`}>User Commands</h2>
            {/* Debug info */}
            {console.log('[RENDER-DEBUG] Active session:', activeSession, 'User commands count:', userCommands.length)}
            {activeSession ? (
              userCommands.length === 0 ? (
                <div className={`${currentTheme.textSecondary} text-xs`}>No commands executed yet.</div>
              ) : (
                <div className="space-y-1">
                  {userCommands.map((cmd, index) => (
                    <div
                      key={cmd.id}
                      className={`p-2 cursor-pointer rounded text-xs transition-colors duration-200 ${
                        selectedCommand?.id === cmd.id
                          ? 'bg-[#06b6d4] border-l-4 border-[#06b6d4] text-white'
                          : `${currentTheme.hover} ${currentTheme.textSecondary} hover:${currentTheme.text}`
                      }`}
                      onClick={() => handleCommandClick(cmd)}
                    >
                      {cmd.stepName && (
                        <div className="text-xs opacity-75 mb-1 truncate">
                          {cmd.stepName}
                        </div>
                      )}
                      <div className="font-mono">
                        {index + 1}. {cmd.command}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className={`${currentTheme.textSecondary} text-xs`}>Select a session to view commands.</div>
            )}
          </div>
        </div>

        {/* Graph and Chat Container - Resizable */}
        <div className="flex-1 flex min-h-0 graph-chat-container">
          {/* Middle Column - Graph Dashboard (Full Height) */}
          <div 
            className={`${currentTheme.cardBg} ${currentTheme.border} border-r p-3 overflow-y-auto scrollbar-hide transition-colors duration-300`}
            style={{ width: `${graphChatSplit}%` }}
          >
          <div className="flex items-center justify-between mb-2">
            <h2 className={`font-bold text-sm ${currentTheme.text}`}>
              {viewMode === 'graph' ? 'Graph View' : 'Text View'}
            </h2>
            {/* View Controls */}
            <div className="flex items-center gap-2">
              {/* Graph Summary Button */}
              {activeSession && (
                <button
                  onClick={requestGraphAnalysis}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 ${currentTheme.accent} hover:${currentTheme.accentHover} text-white`}
                  title="Graph Summary"
                >
                  📊 Summary
                </button>
              )}
              {/* Two Button Toggle */}
              <div className="flex bg-gray-200 rounded-md p-0.5">
                <button
                  onClick={() => setViewMode('graph')}
                  className={`px-2 py-1 rounded-sm transition-colors duration-200 ${
                    viewMode === 'graph' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Graph View"
                >
                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <circle cx="3" cy="3" r="2"/>
                    <circle cx="13" cy="3" r="2"/>
                    <circle cx="3" cy="13" r="2"/>
                    <circle cx="13" cy="13" r="2"/>
                    <circle cx="8" cy="8" r="2"/>
                    <path d="M5 3h6M3 5v6M13 5v6M5 13h6M5 5l6 6M11 5L5 11" stroke="currentColor" strokeWidth="1" fill="none"/>
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('indented')}
                  className={`px-2 py-1 rounded-sm transition-colors duration-200 ${
                    viewMode === 'indented' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Text View"
                >
                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2 2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2zm1 0v12h10V2H3z"/>
                    <path d="M5 4h6v1H5V4zm0 2h6v1H5V6zm0 2h6v1H5V8zm0 2h6v1H5v-1z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {activeSession ? (
            <div className="flex flex-col h-full">
              {/* Graph/Indented View Section */}
              <div className="flex gap-4 flex-1 min-h-0">
                {/* Main View Area */}
                <div className={`${selectedNode ? 'flex-1' : 'w-full'} flex flex-col`}>
                  {graphLoading ? (
                    <div className={`${currentTheme.textSecondary} text-center flex items-center justify-center py-8`}>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#40B5AD] mr-2"></div>
                      Loading workflow context...
                    </div>
                  ) : (
                    <>
                      {viewMode === 'graph' ? (
                        // Graph View
                        <div className="flex flex-col flex-1">
                          <div className={`${currentTheme.borderLight} border rounded-lg ${currentTheme.cardBg} transition-colors duration-300`} style={{ height: '650px', overflow: 'hidden' }}>
                            <VisNetworkGraphComponent 
                              graphData={graphData} 
                              selectedCommand={selectedCommand}
                              onNodeClick={handleNodeClick}
                              userPath={extractUserPath(messages, graphData)}
                            />
                          </div>
                          {/* Graph Legend */}
                          <div className={`${currentTheme.cardBg} ${currentTheme.border} border rounded-lg p-3 mt-4 transition-colors duration-300 flex-shrink-0`}>
                            <GraphLegend />
                          </div>
                        </div>
                      ) : (
                        // Text View
                        <div className="flex flex-col flex-1">
                          <div className={`${currentTheme.borderLight} border rounded-lg ${currentTheme.cardBg} transition-colors duration-300 overflow-auto`} style={{ maxHeight: 'calc(100vh - 350px)', minHeight: '200px' }}>
                            {renderIndentedView()}
                          </div>
                          {/* Text View Legend */}
                          <div className={`${currentTheme.cardBg} ${currentTheme.border} border rounded-lg p-3 mt-3 transition-colors duration-300 flex-shrink-0`}>
                            <h3 className={`text-sm font-semibold ${currentTheme.text} mb-2`}>Text View Legend</h3>
                            <div className={`text-sm ${currentTheme.textSecondary} text-left`}>
                            ⤷Only displays traversed nodes/steps
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Right side - Node Details (Smaller) */}
                {selectedNode && (
                  <div className="w-60 flex-shrink-0">
                    <div className={`text-sm font-semibold ${currentTheme.text} mb-3 flex items-center justify-between`}>
                      <span>Node Details</span>
                      <button
                        onClick={() => setSelectedNode(null)}
                        className={`${currentTheme.hover} rounded-full w-6 h-6 flex items-center justify-center ${currentTheme.textSecondary} hover:${currentTheme.text} transition-colors duration-200`}
                        title="Close node details"
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M18 6L6 18"/>
                          <path d="m6 6 12 12"/>
                        </svg>
                      </button>
                    </div>
                    <NodeDetailsPanel nodeData={selectedNode} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={`${currentTheme.textSecondary} text-center py-8`}>
              <div className="mb-2">📊</div>
              <div>Select an escalated session to view its workflow graph</div>
            </div>
          )}
          </div>

          {/* Resize Handle */}
          <div 
            className="graph-chat-resize-handle"
            onMouseDown={handleGraphChatResizeStart}
            title="Drag to resize"
          />

          {/* Right Column - User-Agent Chatbox (Full Height) */}
          <div 
            className="flex flex-col min-h-0"
            style={{ width: `${100 - graphChatSplit}%` }}
          >
          <div className={`px-4 py-2 ${currentTheme.border} border-b ${currentTheme.headerBg} font-bold text-sm ${currentTheme.text} flex items-center justify-between min-w-full flex-shrink-0 transition-colors duration-300`} style={{ width: '100%' }}>
            <div>
              {activeSession
                ? `Live Chat: ${(() => {
                    const nm = activeSessionUserInfo?.name || activeSession.slice(-8);
                    return nm.charAt(0).toUpperCase() + nm.slice(1).toLowerCase();
                  })()}`
                : "Select a session to join"}
            </div>
            {activeSession && (
              <button
                onClick={requestConversationSummary}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 ${currentTheme.accent} hover:${currentTheme.accentHover} text-white`}
                title="Get conversation summary"
              >
                📋 Summary
              </button>
            )}
          </div>
          <div
            ref={chatBodyRef}
            className={`flex-1 overflow-y-auto p-3 ${currentTheme.contentBg} min-h-0 scrollbar-hide transition-colors duration-300`}
            style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
          >
            {activeSession ? (
              messages.length === 0 ? (
                <div className={currentTheme.textSecondary}>No messages yet.</div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`mb-3 flex justify-end`}
                  >
                    <div
                      className={
                        msg.sender === "user"
                          ? 'px-4 py-2 rounded-xl shadow transition-colors duration-200 text-sm max-w-[70%] bg-[#06b6d4] text-blue-100'
                          : msg.sender === "agent"
                            ? 'px-4 py-2 rounded-xl shadow transition-colors duration-200 text-sm max-w-[80%] ml-auto bg-white text-gray-900 border border-gray-200'
                            : `px-4 py-2 rounded-xl shadow transition-colors duration-200 text-sm max-w-[80%] ml-auto ${currentTheme.buttonBg} ${currentTheme.text}`
                      }
                      style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold">
                          {msg.sender === "agent"
                            ? "You"
                            : msg.sender === "user"
                            ? (() => {
                                const nm = activeSessionUserInfo?.name || "User";
                                return nm.charAt(0).toUpperCase() + nm.slice(1).toLowerCase();
                              })()
                            : msg.sender_name || "AI Agent"}
                        </span>
                        {msg.timestamp && (
                          <span className={`text-xs ${currentTheme.timestampColor} opacity-70 ml-2`}>
                            {formatTimestamp(msg.timestamp)}
                          </span>
                        )}
                      </div>
                      <div>
                        {msg.sender === "bot" || (!msg.sender && msg.text) ? (
                          <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                        ) : (
                          msg.text
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : (
              <div className={`${currentTheme.textSecondary} mt-10 text-center`}>
                Select a session to view and chat.
              </div>
            )}
          </div>
          {/* Input */}
          {activeSession && (
            <div className={`p-3 ${currentTheme.border} border-t ${currentTheme.headerBg} flex gap-2 flex-shrink-0 transition-colors duration-300`}>
              <input
                className={`flex-1 ${currentTheme.borderLight} border ${currentTheme.inputBg} ${currentTheme.text} rounded-full px-4 py-2 focus:outline-none focus:border-gray-300 text-sm ${currentTheme.textMuted} placeholder:${currentTheme.textMuted} transition-colors duration-300`}
                type="text"
                placeholder="Type your message…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              />
              <button
                className={`bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-6 py-2 font-semibold transition-colors duration-200`}
                onClick={sendMessage}
              >
                Send
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Command Output Popup */}
      {selectedCommand && showCommandOutput && (
        <>
          <div className="popup-overlay" onClick={() => setShowCommandOutput(false)} />
          <div className={`command-popup ${currentTheme.cardBg} ${currentTheme.borderLight} border rounded-lg shadow-xl`}>
            <div className={`p-4 ${currentTheme.borderLight} border-b flex justify-between items-center`}>
              <h3 className={`font-semibold ${currentTheme.text}`}>Command Output</h3>
              <button
                onClick={() => setShowCommandOutput(false)}
                className={`${currentTheme.hover} rounded-full w-8 h-8 flex items-center justify-center ${currentTheme.textSecondary} hover:${currentTheme.text} transition-colors duration-200`}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 6L6 18"/>
                  <path d="m6 6 12 12"/>
                </svg>
              </button>
            </div>
            <div className="p-4">
              {selectedCommand.stepName && (
                <div className={`${currentTheme.textSecondary} text-sm mb-2 font-medium`}>
                  Step: {selectedCommand.stepName}
                </div>
              )}
              <div className={`${currentTheme.buttonBg} p-2 rounded mb-3 font-mono text-xs`}>
                <span className={`${currentTheme.text}`}>{selectedCommand.command}</span>
              </div>
              <div className="bg-black text-green-400 p-3 rounded font-mono text-xs overflow-y-auto" style={{ maxHeight: '60vh' }}>
                <pre className="whitespace-pre-wrap">{selectedCommand.output || 'No output available'}</pre>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Conversation Summary Popup */}
      {showSummary && (
        <>
          <div className="popup-overlay" onClick={() => setShowSummary(false)} />
          <div className={`command-popup ${currentTheme.cardBg} ${currentTheme.borderLight} border rounded-lg shadow-xl`}>
            <div className={`p-4 ${currentTheme.borderLight} border-b flex justify-between items-center`}>
              <h3 className={`font-semibold ${currentTheme.text}`}>Conversation Summary</h3>
              <button
                onClick={() => setShowSummary(false)}
                className={`${currentTheme.hover} rounded-full w-8 h-8 flex items-center justify-center ${currentTheme.textSecondary} hover:${currentTheme.text} transition-colors duration-200`}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 6L6 18"/>
                  <path d="m6 6 12 12"/>
                </svg>
              </button>
            </div>
            <div className="p-4">
              {summaryLoading ? (
                <div className={`${currentTheme.textSecondary} text-center py-8`}>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#40B5AD] mx-auto mb-2"></div>
                  Generating summary...
                </div>
              ) : (
                <div className={`${currentTheme.text} text-sm leading-relaxed whitespace-pre-wrap`}>
                  {conversationSummary || 'No summary available'}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Graph Summary Popup */}
      {showGraphSummary && (
        <>
          <div className="popup-overlay" onClick={() => setShowGraphSummary(false)} />
          <div className={`command-popup ${currentTheme.cardBg} ${currentTheme.borderLight} border rounded-lg shadow-xl`}>
            <div className={`p-4 ${currentTheme.borderLight} border-b flex justify-between items-center`}>
              <h3 className={`font-semibold ${currentTheme.text}`}>Graph Summary</h3>
              <button
                onClick={() => setShowGraphSummary(false)}
                className={`${currentTheme.hover} rounded-full w-8 h-8 flex items-center justify-center ${currentTheme.textSecondary} hover:${currentTheme.text} transition-colors duration-200`}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 6L6 18"/>
                  <path d="m6 6 12 12"/>
                </svg>
              </button>
            </div>
            <div className="p-4">
              {graphSummaryLoading ? (
                <div className={`${currentTheme.textSecondary} text-center py-8`}>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#40B5AD] mx-auto mb-2"></div>
                  Analyzing graph and generating AI insights...
                </div>
              ) : (() => {
                const sessionData = activeSession ? 
                  escalatedSessions.find(sess => sess.sessionId === activeSession) : null;
                const visitedNodes = sessionData?.visitedNodes || [];
                
                if (visitedNodes.length === 0) {
                  return (
                    <div className={`${currentTheme.textSecondary} text-center py-4`}>
                      No nodes traversed yet
                    </div>
                  );
                }

                // Analyze visited nodes
                const totalNodes = visitedNodes.length;
                const diagnosisNodes = visitedNodes.filter(node => 
                  node.toLowerCase().includes('diagnosis') || 
                  node.toLowerCase().includes('troubleshoot') ||
                  node.toLowerCase().includes('check')
                ).length;
                const recommendationNodes = visitedNodes.filter(node => 
                  node.toLowerCase().includes('recommend') || 
                  node.toLowerCase().includes('solution') ||
                  node.toLowerCase().includes('fix')
                ).length;
                const otherNodes = totalNodes - diagnosisNodes - recommendationNodes;

                // Generate insights
                const insights = [];
                if (diagnosisNodes > recommendationNodes) {
                  insights.push("🔍 More diagnostic steps taken - thorough investigation");
                }
                if (recommendationNodes > 0) {
                  insights.push("💡 Solutions/recommendations provided");
                }
                if (totalNodes > 5) {
                  insights.push("📊 Complex troubleshooting path - multiple steps involved");
                } else if (totalNodes <= 3) {
                  insights.push("⚡ Quick resolution - minimal steps required");
                }

                return (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="text-center mb-4">
                      <div className={`text-center p-4 rounded-lg ${currentTheme.buttonBg} ${currentTheme.border}`}>
                        <div className={`text-3xl font-bold ${currentTheme.text}`}>{totalNodes}</div>
                        <div className={`text-sm ${currentTheme.textSecondary}`}>Total Steps</div>
                      </div>
                    </div>

                    {/* Traversed Path */}
                    <div>
                      <h4 className={`font-semibold ${currentTheme.text} mb-2`}>Traversed Path:</h4>
                      <div className={`${currentTheme.buttonBg} p-3 rounded-lg max-h-32 overflow-y-auto`}>
                        {visitedNodes.map((node, index) => (
                          <div key={index} className={`text-sm ${currentTheme.textSecondary} mb-1`}>
                            {index + 1}. {node}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* AI Analysis */}
                    {aiGraphAnalysis && (
                      <div>
                        <h4 className={`font-semibold ${currentTheme.text} mb-2`}>Summary:</h4>
                        <div className={`${currentTheme.buttonBg} p-3 rounded-lg border-l-4 border-[#40B5AD]`}>
                          <div className={`${currentTheme.text} text-sm leading-relaxed`}>
                            {aiGraphAnalysis}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default AgentDashboard;